/**
 * Branding Utilities
 *
 * Applies per-organization branding (colors, logo, tagline) to CSS custom
 * properties that the rest of the UI consumes. Non-breaking: falls back to
 * default Kids WiseApp branding when any field is missing.
 */

const DEFAULT_BRANDING = {
  primary_color: '#00BCD4',
  secondary_color: '#FFC107',
  logo_url: null,
  welcome_message: null,
  tagline: null,
  name: 'Kids WiseApp',
  // The historical "platform" slug is preserved so the seeded demo org
  // (which deliberately keeps its old slug for routing stability) still
  // resolves. The user-visible *name* is what changes for the rebrand.
  slug: 'wizkids-wiseapp',
};

/**
 * Convert a hex colour to the `H S% L%` triplet Tailwind tokens expect.
 * Falls back to the raw hex if parsing fails (browsers still accept hex).
 */
function hexToHslTriplet(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const clean = hex.trim().replace('#', '');
  if (clean.length !== 3 && clean.length !== 6) return null;

  const full = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean;

  const r = parseInt(full.substring(0, 2), 16) / 255;
  const g = parseInt(full.substring(2, 4), 16) / 255;
  const b = parseInt(full.substring(4, 6), 16) / 255;

  if ([r, g, b].some((v) => Number.isNaN(v))) return null;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = ((b - r) / d + 2); break;
      case b: h = ((r - g) / d + 4); break;
      default: break;
    }
    h *= 60;
  }

  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Apply branding CSS variables to :root. These override the default Kids WiseApp
 * palette defined in index.css.
 */
export function applyBranding(branding) {
  const b = { ...DEFAULT_BRANDING, ...(branding || {}) };
  const root = document.documentElement;

  const primaryHsl = hexToHslTriplet(b.primary_color);
  const secondaryHsl = hexToHslTriplet(b.secondary_color);

  if (primaryHsl) {
    root.style.setProperty('--primary', primaryHsl);
    root.style.setProperty('--ring', primaryHsl);
    root.style.setProperty('--sidebar-primary', primaryHsl);
    root.style.setProperty('--chart-1', primaryHsl);
  }
  if (secondaryHsl) {
    root.style.setProperty('--secondary', secondaryHsl);
    root.style.setProperty('--accent', secondaryHsl);
    root.style.setProperty('--chart-2', secondaryHsl);
  }

  // Keep raw hex values around too, in case components want the raw color.
  if (b.primary_color) root.style.setProperty('--brand-primary-hex', b.primary_color);
  if (b.secondary_color) root.style.setProperty('--brand-secondary-hex', b.secondary_color);

  // Persist so reloads feel instant (pre-auth).
  try {
    localStorage.setItem('org_branding', JSON.stringify(b));
  } catch (err) { /* ignore quota / unavailable */ }
}

export function clearBranding() {
  const root = document.documentElement;
  [
    '--primary', '--ring', '--sidebar-primary', '--chart-1',
    '--secondary', '--accent', '--chart-2',
    '--brand-primary-hex', '--brand-secondary-hex',
  ].forEach((prop) => root.style.removeProperty(prop));
  try { localStorage.removeItem('org_branding'); } catch (err) { /* ignore */ }
}

export function getCachedBranding() {
  try {
    const raw = localStorage.getItem('org_branding');
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

export { DEFAULT_BRANDING };
